/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { DEPARTMENTS, type Department, type PrepItem, type PrepList } from "../shared/types";
import { WeekdayPicker } from "../shared/WeekdayPicker";

type Props = { listId: number | null; onClose: (changed: boolean) => void };

const newItem = (): PrepItem => ({ name: "", par_qty: 0, unit: null, notes: null });

// A prep list: what should be ready before service (mise en place) and how
// much of each — the par.
export const PrepListEditor = defineComponent<Props>({
  name: "PrepListEditor",
  props: ["listId", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(props.listId != null);
    const saving = ref(false);
    const name = ref("");
    const department = ref<Department>("kitchen");
    const days = ref<number[]>([1, 2, 3, 4, 5, 6, 7]);
    const active = ref(true);
    const items = ref<PrepItem[]>([newItem(), newItem(), newItem()]);

    onMounted(async () => {
      if (props.listId == null) return;
      try {
        const l = (await trpc.workday.prep.get.query({ id: props.listId })) as PrepList;
        name.value = l.name;
        department.value = l.department;
        days.value = l.schedule_days;
        active.value = l.active;
        items.value = [...l.items, newItem()];
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
        props.onClose(false);
      } finally {
        loading.value = false;
      }
    });

    const filled = computed(() => items.value.filter((i) => i.name.trim()));
    const set = (i: number, patch: Partial<PrepItem>) => {
      items.value = items.value.map((x, j) => (j === i ? { ...x, ...patch } : x));
      // Always keep an empty row at the end for quick entry.
      if (i === items.value.length - 1 && patch.name) items.value = [...items.value, newItem()];
    };

    const save = async () => {
      saving.value = true;
      try {
        await trpc.workday.prep.save.mutate({
          id: props.listId ?? undefined,
          name: name.value.trim(),
          department: department.value,
          schedule_days: days.value,
          active: active.value,
          items: filled.value.map((i) => ({
            id: i.id,
            name: i.name.trim(),
            par_qty: Number(i.par_qty) || 0,
            unit: i.unit?.trim() || null,
            notes: i.notes?.trim() || null,
          })),
        });
        toast.showToast(t("workday.common.saved"), { type: "success" });
        props.onClose(true);
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={props.listId ? t("workday.prep.edit_title") : t("workday.prep.new_title")}
        onClose={() => props.onClose(false)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose(false)}>
                {t("general.words.cancel")}
              </button>
              <button class="btn btn-primary" disabled={saving.value || !name.value.trim() || !filled.value.length} onClick={save}>
                {t("workday.common.save")}
              </button>
            </div>
          </div>
        )}
      >
        {loading.value ? (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        ) : (
          <div class="space-y-5 p-6">
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
              <label class="block">
                <span class="mb-1 block text-sm font-medium">{t("workday.common.name")}</span>
                <input class="input input-bordered w-full" v-model={name.value} placeholder={t("workday.prep.name_placeholder")} />
              </label>
              <label class="block">
                <span class="mb-1 block text-sm font-medium">{t("workday.common.department_label")}</span>
                <select class="select select-bordered w-full" value={department.value} onChange={(e: Event) => (department.value = (e.target as HTMLSelectElement).value as Department)}>
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {t(`workday.department.${d}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <span class="mb-1 block text-sm font-medium">{t("workday.schedule.title")}</span>
              <WeekdayPicker days={days.value} onChange={(d: number[]) => (days.value = d)} />
            </div>
            <div>
              <div class="mb-1 text-sm font-medium">{t("workday.prep.items")}</div>
              <p class="mb-2 text-xs text-base-content/60">{t("workday.prep.items_help")}</p>
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{t("workday.prep.item")}</th>
                    <th class="w-24 text-right">{t("workday.prep.par")}</th>
                    <th class="w-24">{t("workday.prep.unit")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.value.map((item, i) => (
                    <tr key={item.id ?? `n${i}`}>
                      <td class="pl-0">
                        <input
                          class="input input-bordered input-sm w-full"
                          placeholder={i === items.value.length - 1 ? t("workday.prep.add_item_placeholder") : ""}
                          value={item.name}
                          onInput={(e: Event) => set(i, { name: (e.target as HTMLInputElement).value })}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          class="input input-bordered input-sm w-full text-right"
                          value={item.par_qty || ""}
                          onInput={(e: Event) => set(i, { par_qty: Number((e.target as HTMLInputElement).value) })}
                        />
                      </td>
                      <td>
                        <input
                          class="input input-bordered input-sm w-full"
                          placeholder="kg, l, tray…"
                          value={item.unit ?? ""}
                          onInput={(e: Event) => set(i, { unit: (e.target as HTMLInputElement).value })}
                        />
                      </td>
                      <td class="pr-0">
                        {item.name && (
                          <button class="btn btn-ghost btn-xs btn-square" aria-label={t("workday.common.remove")} onClick={() => (items.value = items.value.filter((_, j) => j !== i))}>
                            <span class="material-symbols-outlined text-base">close</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {props.listId != null && (
              <label class="flex items-center gap-2 text-sm">
                <input type="checkbox" class="toggle toggle-sm" v-model={active.value} />
                {t("workday.common.active_help")}
              </label>
            )}
          </div>
        )}
      </LemmonModal>
    );
  },
});
